import React, { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Typography from "@tiptap/extension-typography";
import Highlight from "@tiptap/extension-highlight";
import Table from "@tiptap/extension-table";
import { Node } from "@tiptap/core";

import {
  TextB,
  TextItalic,
  TextUnderline,
  TextStrikethrough,
  ListBullets,
  ListNumbers,
  Quotes,
  LinkSimple,
  ArrowUUpLeft,
  ArrowUUpRight,
  TextH,
  TextHTwo,
  TextHOne,
  TextHThree,
  BookOpenText,
} from "@phosphor-icons/react";

import "./RichTextEditor.css";
import { Box, Menu, MenuButton, MenuItem, MenuList, Text } from "@chakra-ui/react";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell"; // optional, for styling

/**
 * Custom Cite extension for handling AI-generated citations in the rich text editor.
 */
const Cite = Node.create({
  name: "cite",

  group: "inline",
  inline: true,
  content: "inline*",

  addOptions() {
    return {
      ...this.parent?.(),
      onClick: (event) => {
        // doesnt work but harmless
        console.log("Cite clicked", event, this);
      }, // Default no-op function
    };
  },

  addAttributes() {
    const attributes = {};
    // preserve attributes
    for (const attr of ["title", "data-source_id", "data-question_id", "data-page", "data-url"]) {
      attributes[attr] = {
        default: null,
        parseHTML: (element) => element.getAttribute(attr),
        renderHTML: (attributes) => {
          if (!attributes[attr]) return {};
          return { [attr]: attributes[attr] };
        },
      };
    }
    return attributes;
  },

  parseHTML() {
    return [
      {
        tag: "cite",
        getAttrs: (element) => {
          // Preserve all attributes from the cite element
          const attrs = {};
          for (let i = 0; i < element.attributes.length; i++) {
            const attr = element.attributes[i];
            attrs[attr.name] = attr.value;
          }
          return attrs;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "cite",
      {
        ...HTMLAttributes,
        // class: "cite-element",
      },
      0,
    ];
  },

  addCommands() {
    // TODO
    return {
      setCite:
        (attributes) =>
        ({ commands }) => {
          return commands.wrapIn(this.name, attributes);
        },
      toggleCite:
        (attributes) =>
        ({ commands }) => {
          return commands.toggleWrap(this.name, attributes);
        },
      unsetCite:
        () =>
        ({ commands }) => {
          return commands.lift(this.name);
        },
    };
  },
});

const ToolBar = ({ editor, toolbarRight }) => {
  const toggleLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <Box className="toolbar">
      <div className="toolbar-left">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive("bold") ? "active" : ""}
        >
          <TextB size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive("italic") ? "active" : ""}
        >
          <TextItalic size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive("underline") ? "active" : ""}
        >
          <TextUnderline size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={editor.isActive("strike") ? "active" : ""}
        >
          <TextStrikethrough size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive("bulletList") ? "active" : ""}
        >
          <ListBullets size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive("orderedList") ? "active" : ""}
        >
          <ListNumbers size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive("blockquote") ? "active" : ""}
        >
          <Quotes size={18} />
        </button>
        <button onClick={toggleLink} className={editor.isActive("link") ? "active" : ""}>
          <LinkSimple size={18} />
        </button>
        {/* <button
        onClick={() => editor.chain().focus().toggleCite().run()}
        className={editor.isActive("cite") ? "active" : ""}
        title="Toggle citation"
      >
        <BookOpenText size={18} />
      </button> */}
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={editor.isActive("heading", { level: 1 }) ? "active" : ""}
        >
          <TextHOne size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive("heading", { level: 2 }) ? "active" : ""}
        >
          <TextHTwo size={18} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editor.isActive("heading", { level: 3 }) ? "active" : ""}
        >
          <TextHThree size={18} />
        </button>
        <button onClick={() => editor.chain().focus().undo().run()}>
          <ArrowUUpLeft size={18} />
        </button>
        <button onClick={() => editor.chain().focus().redo().run()}>
          <ArrowUUpRight size={18} />
        </button>

        <Menu size="sm">
          <MenuButton fontSize="body.s">Table</MenuButton>
          <MenuList>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            >
              Insert table
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().addColumnBefore().run()}
              disabled={!editor.can().addColumnBefore()}
            >
              Add column before
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              disabled={!editor.can().addColumnAfter()}
            >
              Add column after
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().deleteColumn().run()}
              disabled={!editor.can().deleteColumn()}
            >
              Delete column
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().addRowBefore().run()}
              disabled={!editor.can().addRowBefore()}
            >
              Add row before
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().addRowAfter().run()}
              disabled={!editor.can().addRowAfter()}
            >
              Add row after
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().deleteRow().run()}
              disabled={!editor.can().deleteRow()}
            >
              Delete row
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().deleteTable().run()}
              disabled={!editor.can().deleteTable()}
            >
              Delete row
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().deleteTable().run()}
              disabled={!editor.can().deleteTable()}
            >
              Delete table
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().mergeCells().run()}
              disabled={!editor.can().mergeCells()}
            >
              Merge cells
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().splitCell().run()}
              disabled={!editor.can().splitCell()}
            >
              Split cell
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().toggleHeaderRow().run()}
              disabled={!editor.can().toggleHeaderRow()}
            >
              Header row
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
              disabled={!editor.can().toggleHeaderColumn()}
            >
              Header column
            </MenuItem>
            <MenuItem
              fontSize="body.xs"
              onClick={() => editor.chain().focus().setCellAttribute("backgroundColor", "#FAF594").run()}
              disabled={!editor.can().setCellAttribute("backgroundColor", "#FAF594")}
            >
              Highlight cell
            </MenuItem>
          </MenuList>
        </Menu>
      </div>
      <Box>{toolbarRight}</Box>
    </Box>
  );
};

const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),

      backgroundColor: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-background-color"),
        renderHTML: (attributes) => {
          return {
            "data-background-color": attributes.backgroundColor,
            style: `background-color: ${attributes.backgroundColor}`,
          };
        },
      },
    };
  },
});

/**
 *
 * @param {*} text
 * @returns true if no html tags
 */
const isPlainText = (text) => {
  if (!text) return true;
  const htmlRegex = /<[a-z][\s\S]*>/i;
  return !htmlRegex.test(text);
};

/**
 *
 * @param {Object} props
 * @param {!string} props.content
 * @param {function} props.onChange Called with the editor HTML content (not the change event)
 * @param {boolean} props.isDisabled
 * @param {string|number} props.height Optional height for the editor (e.g., "400px", "50vh", 400)
 * @param {function} props.toolbarRight
 * @param {function?} props.onElementClick (tagName, domElement, attributes, event) => void Called when an element in the text is clicked, eg a Cite
 * @returns
 */
const RichTextEditor = ({
  content= "",
  onChange,
  isDisabled = false,
  isToolbarDisabled = false,
  height,
  toolbarRight,
  onElementClick,
  isBorderRounded = true,
  isViewer = false,
  customStyles = {},
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        history: true,
        // Allow cite tags to be preserved
        document: {
          content: "block+",
        },
      }),
      Underline,
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
      }),
      Typography,
      Highlight,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      CustomTableCell,
      Cite,
    ],
    content: content || "",
    parseOptions: {
      preserveWhitespace: "full",
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange && onChange(html);
    },
    editable: !isDisabled,
  });

  useEffect(() => {
    // convert to html (otherwise plain formatting like paragraphs are lost)
    let htmlContent = content || "";
    if (isPlainText(content)) {
      // crude - but do we want anything more?
      htmlContent = content.replace(/\n\n/g, "<br/><br/>");
    }
    if (editor && htmlContent !== editor.getHTML()) {
    //   console.log("set htmlContent", htmlContent);
    //   console.log("editor HTML before setContent:", editor.getHTML());
      editor.commands.setContent(htmlContent, false);
    //   console.log("editor HTML after setContent:", editor.getHTML());
    }
  }, [content, editor]);

  // Handle cite element clicks
  const handleEditorClick = (event) => {
    if (!onElementClick) return;

    // HACK Check if the clicked element is or is inside a cite element
    const citeElement = event.target.closest("cite");
    if (citeElement) {
      event.preventDefault();
      event.stopPropagation();
      // Extract attributes from the cite element
      const attributes = {};
      for (let i = 0; i < citeElement.attributes.length; i++) {
        const attr = citeElement.attributes[i];
        attributes[attr.name] = attr.value;
      }
      // Call the onCiteClick callback with the cite element and its attributes
      onElementClick(event?.target?.tagName, citeElement, attributes, event);
    }
  };


  if (!editor) return null;

  // Apply height styling if provided
  const editorStyle = customStyles;
  if (height) {
    // -50px for the toolbar
    editorStyle.height = "calc(100% - 50px)"; //typeof height === "number" ? `${height}px` : height;
  }

  // Build className with conditional "view-only"
    const editorClasses = [
      "editor-content",
      isViewer && "viewer",
    ]
      .filter(Boolean)
      .join(" ");


  return (
    <Box
      border={isViewer ? "none" : "solid 1px"}
      borderColor= "gray.200"
      borderRadius={isBorderRounded ? "8px" : "none"}
      p={0}
      height={height}
    >
      {!isToolbarDisabled && !isViewer && <ToolBar editor={editor} toolbarRight={toolbarRight} />}
      <EditorContent editor={editor} className={editorClasses} style={editorStyle} onClick={handleEditorClick} />
    </Box>
  );
};



export const RichTextViewer = ({ content, height, color}) => {
  const customStyles = {
    color: color,
    height: height,
  }
  return <RichTextEditor content={content} isDisabled={true} isToolbarDisabled={true} isViewer={true} customStyles={customStyles} />
}

export default RichTextEditor;
